import React, { useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { logError } from 'utils/logger';
import { Edit } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { NotesTable } from 'modules/vendorOpportunity/notes/ui/NotesTable';
import { useNotifications } from 'modules/notification';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextareaAutosize } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { UserProfile, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { VONote } from '../types';
import { voNotesApi } from '../api';
import { useVO } from '../../hooks/useVO';

interface Props {
  vo: VendorOpportunity;
  userProfile: UserProfile;
}

export const NotesCard = ({ vo, userProfile }: Props) => {
  const classes = useStyles();

  const { showNotification } = useNotifications();
  const { handleUpdateVO } = useVO();

  const [notes, setNotes] = useState<VONote[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const [openNoteModal, setOpenNoteModal] = useState<boolean>(false);

  useQuery(voNotesApi.getNotes, {
    variables: { id: vo.vendorOpportunityId },
    onCompleted: ({ vendorOpportunity }) => {
      if (_.isEmpty(vendorOpportunity.notes)) return;
      setNotes(JSON.parse(vendorOpportunity.notes));
    },
  });

  const saveVendorOpportunity = async (notes: string) => {
    if (!vo.vendorOpportunityId) return;

    try {
      await handleUpdateVO({ VOId: vo.vendorOpportunityId, fieldsObj: { notes } });
      showNotification('Note saves successfully!');
    } catch (err) {
      logError('[updateVendorOpportunity] Issue with save note', err);
      showNotification('Something went wrong!', { type: 'error' });
      (window as any).NREUM.noticeError(err);
    }
  };

  const handleNoteDelete = async (note: VONote) => {
    const newNotes = [...notes];
    _.remove(newNotes, note);
    await saveVendorOpportunity(JSON.stringify(newNotes));
    setNotes(newNotes);
  };

  const handleNoteUpdate = async (original: VONote, newNote: string) => {
    const newNotes = [...notes];
    const index = _.findIndex(newNotes, original);

    newNotes.splice(index, 1, {
      note: newNote,
      createdOn: original.createdOn,
      createdBy: original.createdBy,
    });

    await saveVendorOpportunity(JSON.stringify(newNotes));
    setNotes(newNotes);
  };

  const handleNoteSave = async () => {
    setOpenNoteModal(false);
    const note: VONote = { note: newNote, createdOn: moment().format(), createdBy: userProfile.fullName! };

    const newNotes = [...notes, note];

    await saveVendorOpportunity(JSON.stringify(newNotes));
    setNotes(newNotes);
    setNewNote('');
  };

  return (
    <>
      <CardContainer
        title="Notes"
        actionBtnProps={{
          startIcon: <Edit />,
          label: 'ADD NOTE',
          onClick: () => {
            setOpenNoteModal(true);
          },
        }}
      >
        <NotesTable notes={notes} onNoteDelete={handleNoteDelete} onNoteUpdate={handleNoteUpdate} />
      </CardContainer>

      <Dialog open={openNoteModal} onClose={() => setOpenNoteModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Create New Note</DialogTitle>

        <DialogContent>
          <TextareaAutosize
            placeholder="Enter note..."
            className={classes.notesBox}
            onChange={e => setNewNote(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleNoteSave}>
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  notesBox: {
    width: '100%',
    resize: 'none',
    padding: '5px',
    overflowY: 'auto',
    marginTop: '10px',
    minHeight: '100px',
    maxHeight: '200px',
    borderRadius: '10px',
    border: '1px solid #DDD',
  },
});
